// QR Kiosk v2 — Tier-2 dashboard page (v1 KioskDashboard parity). The dashboard
// IS the menu: no KPage, no title row, no back bar. Params → fetch → route-scoped
// machine guard → §5 tree (cached-dimmed / normal / full error / skeleton) → blocks.
// liveStatus is passed undefined at both call sites (PG-1); B4-T4 wires it later.
// Pass 1b 2026-07-24 (KV2_DESIGN_REVIEW_FEEDBACK.md §1b): stack is header (now a
// clickable spec-sheet button) → full-width fault CTA → exact 2×2 tile board
// (open | archive / tasks | manuals) → composer dock. Gold Ask-MIDAS launcher
// DELETED (§1b.2 — MIDAS stays reachable via the bottom composer); company +
// machine-details tiles moved to Settings and the header card respectively.
import React, { useEffect } from "react";
import { useParams } from "react-router-dom";
import { useAppDispatch } from "../../../shared/hooks/sharedHooks";
import useTranslation from "../../../shared/hooks/useTranslation";
import { getMachine, getServiceReports } from "../../publicSlice";
import { ErrorCallout } from "../shared/ErrorCallout";
import { CachedErrorState } from "../shared/CachedErrorState";
import { useSurfaceState } from "../shared/useSurfaceState";
import { useKioskRouteMachine } from "../shared/useKioskRouteMachine";
import { formatClockTime } from "../shared/clock";
import DashboardSkeleton from "./DashboardSkeleton";
import DashboardHeaderCard from "./DashboardHeaderCard";
import DashboardActionRows from "./DashboardActionRows";
import DashboardTileGrid from "./DashboardTileGrid";
import MidasComposerDock from "./MidasComposerDock";

export function KioskDashboardV2() {
  const { id } = useParams(); // :id = machine internalNumber (QR/business number, NEVER the GUID)
  const dispatch = useAppDispatch();
  const { translate } = useTranslation("public");

  useEffect(() => {
    if (id) dispatch(getMachine(id));
  }, [id, dispatch]);

  // Route-scoped machine (KV2-1-e) via the shared resolver: ignore the single shared
  // publicSlice.machine field until it belongs to THIS route, and backfill the
  // internalNumber that a guest's curated payload omits (else this page — and its
  // reports prefetch + every tile/CTA nav — hangs on skeleton forever; see
  // useKioskRouteMachine).
  const m = useKioskRouteMachine(id);

  // Reports prefetch — v1 verbatim (warms the KV2-2 list cache).
  useEffect(() => {
    if (m?.internalNumber) {
      dispatch(
        getServiceReports({ machineNumber: m.internalNumber, query: { statusCodes: ["InProgress", "Solved"] } })
      );
    }
  }, [m?.internalNumber, dispatch]);

  const st = useSurfaceState("machine", id);

  // §5 tree, no-route-valid-machine half: full error only when THIS key failed;
  // otherwise skeleton (covers idle AND pending → kills the one-frame flash, BUG-1).
  if (!m) {
    if (st.failed) {
      return (
        <ErrorCallout
          title={translate("kioskV2DashboardLoadFailedTitle")}
          hint={translate("kioskV2DashboardLoadFailedHint")}
          onRetry={id ? () => dispatch(getMachine(id)) : undefined}
          retryLabel={translate("kioskV2Retry")}
        />
      );
    }
    return <DashboardSkeleton />;
  }

  const openCount = m.openServiceReportsCount || 0;

  const content = (
    <>
      <div className="kv2-enter" style={{ animationDelay: "0s", flexShrink: 0 }}>
        <DashboardHeaderCard machine={m} liveStatus={undefined} />
      </div>
      <div className="kv2-enter" style={{ animationDelay: "0.05s", flexShrink: 0 }}>
        <DashboardActionRows machine={m} />
      </div>
      {/* Exact 2×2 tile board (§1b.8) — open | archive / tasks (self-hiding) | manuals. */}
      <div className="kv2-enter" style={{ animationDelay: "0.1s", flexShrink: 0 }}>
        <DashboardTileGrid machine={m} openCount={openCount} />
      </div>
      <MidasComposerDock machine={m} liveStatus={undefined} />
    </>
  );

  // Cached machine + this key's refetch failed → dim the whole body under the callout.
  if (st.failed) {
    return (
      <CachedErrorState
        title={translate("kioskV2DashboardLoadFailedTitle")}
        hint={translate("kioskV2DashboardLoadFailedHint")}
        onRetry={id ? () => dispatch(getMachine(id)) : undefined}
        retryLabel={translate("kioskV2Retry")}
        cachedAt={st.loadedAt}
        cachedLabel={st.loadedAt != null ? translate("kioskV2ShowingCachedFrom", { time: formatClockTime(st.loadedAt) }) : undefined}
      >
        {content}
      </CachedErrorState>
    );
  }

  return content;
}

export default KioskDashboardV2;
